<template>
  <!-- Mobile Sidebar that starts below header -->
  <div
    id="hs-sidebar-mobile-nav"
    ref="sidebarRef"
    class="hs-overlay [--auto-close:lg] lg:hidden w-full hs-overlay-open:translate-x-0 -translate-x-full transition-all duration-300 transform fixed start-0 z-50 bg-white"
    role="dialog"
    tabindex="-1"
    aria-label="Mobile Sidebar"
  >
    <div class="h-full max-h-full flex flex-col">
      <!-- Sidebar Content -->
      <div class="flex-1 overflow-y-auto py-6 px-4 pb-8">
        <div class="flex flex-col gap-6">
          <!-- User Balance (for logged in users) -->
          <div
            v-if="isLoggedIn"
            class="flex flex-col items-center bg-background-color rounded-lg gap-4 py-8 px-9 mb-6"
          >
            <p class="text-2xl font-semibold text-gray-800 tracking-wide">
              {{ $t('sidebar.balance') }}
            </p>
            <div class="flex items-center gap-2">
              <Wallet class="size-6 text-gray-500" />
              <p class="text-lg font-medium text-gray-500">
                <template v-if="accountOverviewLoading || btcDisplayLoading">
                  <span class="animate-pulse">{{ $t('common.loading') }}</span>
                </template>
                <template v-else-if="accountOverview">
                  {{ btcDisplayFormatted }}
                </template>
                <template v-else>
                  <span class="text-gray-400">0.00000000 BTC</span>
                </template>
              </p>
            </div>
          </div>

          <!-- Navigation Links -->
          <div>
            <!-- Public Pool -->
            <router-link
              to="/"
              class="flex items-center gap-x-3 py-3 text-lg text-gray-500"
              :class="{ 'text-primary font-medium': isActiveRoute('/') }"
              @click="closeSidebar"
            >
              {{ $t('sidebar.publicPool') }}
            </router-link>

            <!-- Documentation -->
            <router-link
              to="/documentation"
              class="flex items-center gap-x-3 py-3 text-lg text-gray-500"
              :class="{ 'text-primary font-medium': isActiveRoute('/documentation') }"
              @click="closeSidebar"
            >
              {{ $t('sidebar.documentation') }}
            </router-link>

            <!-- Contacts -->
            <router-link
              to="/contacts"
              class="flex items-center gap-x-3 py-3 text-lg text-gray-500 mb-3"
              :class="{ 'text-primary font-medium': isActiveRoute('/contacts') }"
              @click="closeSidebar"
            >
              {{ $t('sidebar.contacts') }}
            </router-link>
          </div>

          <!-- Auth Actions -->
          <div
            class="pt-3 space-y-3 border-t border-gray-200"
            :class="{ 'border-t-0': !isLoggedIn }"
          >
            <router-link
              v-if="isLoggedIn"
              to="/statistics"
              class="flex items-center gap-x-3 py-3 text-lg text-gray-500"
              :class="{ 'text-primary font-medium': isActiveRoute('/statistics') }"
              @click="closeSidebar"
            >
              {{ $t('sidebar.myStatistics') }}
            </router-link>
            <!-- For non-logged in users -->
            <template v-if="!isLoggedIn">
              <router-link to="/login" class="btn-secondary px-4 py-3 w-full" @click="closeSidebar">
                <LogIn class="size-5" />
                {{ $t('auth.login') }}
              </router-link>
              <router-link to="/signup" class="btn-primary px-4 py-3 w-full" @click="closeSidebar">
                <UserPlus class="size-5" />
                {{ $t('auth.signup') }}
              </router-link>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useLogout } from '@/composables/useLogout'
import { useAccountOverviewQuery } from '@/queries/useUser'
import { useAuthStore } from '@/stores/authStore'
import { useCurrencyStore } from '@/stores/currencyStore'
import { lockScroll, unlockScroll } from '@/utils/scroll-lock'
import { LogIn, UserPlus, Wallet } from 'lucide-vue-next'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const authStore = useAuthStore()
const route = useRoute()
const { logout, isLogoutInProgress } = useLogout()
const currencyStore = useCurrencyStore()
const sidebarRef = ref<HTMLElement>()

// Authentication state
const isLoggedIn = computed(() => authStore.isAuthenticated)

// Account overview and balance - use centralized currency
const { data: accountOverview, isLoading: accountOverviewLoading } = useAccountOverviewQuery()

const btcDisplayFormatted = computed(() => {
  const amount = accountOverview.value?.unpaid_amount || '0.00000000'
  return `${amount} ${currencyStore.selectedCurrency}`
})
const btcDisplayLoading = computed(() => false)

// Logout state
const isLoggingOut = computed(() => isLogoutInProgress())

// Helper methods
const isActiveRoute = (path: string) => {
  if (path === '/') {
    return route.path === '/' || route.path === '/public-pool'
  }
  return route.path === path
}

// Функция для принудительной очистки backdrop'а
const forceRemoveBackdrop = () => {
  // Удаляем все backdrop'ы для данного сайдбара
  const backdrops = document.querySelectorAll(
    '#hs-sidebar-mobile-nav-backdrop, [data-hs-overlay-backdrop-template]'
  )
  backdrops.forEach((backdrop) => {
    if (backdrop.parentNode) {
      backdrop.parentNode.removeChild(backdrop)
    }
  })

  // Также ищем backdrop'ы по классу
  const classBackdrops = document.querySelectorAll('.hs-overlay-backdrop')
  classBackdrops.forEach((backdrop) => {
    const element = backdrop as HTMLElement
    // Проверяем, что это backdrop для нашего сайдбара
    if (element.style.zIndex === '49' || element.id === 'hs-sidebar-mobile-nav-backdrop') {
      if (element.parentNode) {
        element.parentNode.removeChild(element)
      }
    }
  })
}

const closeSidebar = () => {
  // Use Preline's overlay close method
  const sidebar = document.getElementById('hs-sidebar-mobile-nav')
  if (sidebar && window.HSOverlay) {
    window.HSOverlay.close(sidebar)
  }

  // Принудительная очистка backdrop'а с задержкой
  setTimeout(() => {
    forceRemoveBackdrop()
    unlockScroll()
  }, 100)

  // Дополнительная очистка через 300ms (после анимации)
  setTimeout(() => {
    forceRemoveBackdrop()
  }, 300)
}

const _handleLogout = async () => {
  if (isLoggingOut.value) return
  closeSidebar()
  await logout(true)
}

// Calculate and set sidebar position
const updateSidebarPosition = () => {
  if (!sidebarRef.value) return

  // Find all elements that should be above the sidebar
  const header = document.querySelector('header')
  const statBar = document.querySelector('div[class*="bg-green-900"]') // TheStatBar

  let topOffset = 0

  if (statBar) {
    topOffset += statBar.getBoundingClientRect().height
  }

  if (header) {
    topOffset += header.getBoundingClientRect().height
  }

  sidebarRef.value.style.top = `${topOffset}px`
  sidebarRef.value.style.height = `calc(100vh - ${topOffset}px)`
}

// MutationObserver для отслеживания создания backdrop'а
let backdropObserver: MutationObserver | null = null

const setupBackdropObserver = () => {
  backdropObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          // Проверяем, что это backdrop для нашего сайдбара
          if (
            node.id === 'hs-sidebar-mobile-nav-backdrop' ||
            (node.classList?.contains('hs-overlay-backdrop') &&
              node.getAttribute('data-hs-overlay-backdrop-template') !== null)
          ) {
            // Добавляем event listener для клика по backdrop'у
            node.addEventListener('click', () => {
              closeSidebar()
            })
          }
        }
      })
    })
  })

  backdropObserver.observe(document.body, {
    childList: true,
    subtree: true
  })
}

onMounted(() => {
  updateSidebarPosition()

  // Update position on window resize
  window.addEventListener('resize', updateSidebarPosition)

  // Настройка observer'а для backdrop'а
  setupBackdropObserver()

  // Add event listeners for HSOverlay events to properly handle backdrop
  const sidebarElement = document.getElementById('hs-sidebar-mobile-nav')
  if (sidebarElement) {
    // Listen for open event
    sidebarElement.addEventListener('open.hs.overlay', () => {
      lockScroll()
    })

    // Listen for close event
    sidebarElement.addEventListener('close.hs.overlay', () => {
      // Принудительная очистка backdrop'а
      setTimeout(() => {
        forceRemoveBackdrop()
        unlockScroll()
      }, 50)
    })
  }

  // Добавляем обработчик кликов по всему документу для закрытия сайдбара
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement

    // Проверяем, что клик по backdrop'у
    if (
      target.id === 'hs-sidebar-mobile-nav-backdrop' ||
      target.classList.contains('hs-overlay-backdrop')
    ) {
      closeSidebar()
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateSidebarPosition)

  // Остановка observer'а
  if (backdropObserver) {
    backdropObserver.disconnect()
    backdropObserver = null
  }

  // Ensure scroll is unlocked when component is unmounted
  unlockScroll()

  // Force remove backdrop
  forceRemoveBackdrop()
})
</script>
